import React, { useMemo } from "react";
import { TrendingDown } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell, ReferenceLine } from "recharts";

const idr = (v) => `Rp ${Math.round(v || 0).toLocaleString("id-ID")}`;
const short = (v) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}jt` : Math.abs(v) >= 1e3 ? `${Math.round(v / 1e3)}rb` : Math.round(v));

// Aggregates day rows (period × agent × provider) into one point per day.
function byDay(rows) {
  const m = {};
  rows.forEach((r) => {
    const x = (m[r.period] ||= { period: r.period, sessions: 0, revenue_idr: 0, provider_cost_idr: 0, infra_cost_idr: 0, margin_idr: 0 });
    x.sessions += r.sessions || 0; x.revenue_idr += r.revenue_idr || 0; x.provider_cost_idr += r.provider_cost_idr || 0; x.infra_cost_idr += r.infra_cost_idr || 0; x.margin_idr += r.margin_idr || 0;
  });
  return Object.values(m).sort((a, b) => a.period.localeCompare(b.period)).map((x) => ({ ...x, day: x.period.slice(5).split("-").reverse().join("/"), margin_pct: x.revenue_idr ? (x.margin_idr / x.revenue_idr) * 100 : null }));
}

function Tip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-xl border border-line bg-white p-3 text-xs shadow-lg">
      <p className="font-bold text-slate-900">{p.period} · {p.sessions} sesi</p>
      <p className="mt-1 text-slate-600">Pendapatan <b>{idr(p.revenue_idr)}</b></p>
      <p className="text-slate-600">Biaya provider {idr(p.provider_cost_idr)} · infra {idr(p.infra_cost_idr)}</p>
      <p className={`font-bold ${p.margin_idr < 0 ? "text-rose-600" : "text-emerald-700"}`}>Margin {idr(p.margin_idr)}{p.margin_pct != null ? ` (${p.margin_pct.toFixed(1)}%)` : ""}</p>
    </div>
  );
}

// rows: null = loading, [] = empty. Data is fetched by VideoCostTab (single request chain) to avoid racing filters.
export function VideoCostChart({ rows }) {
  const data = useMemo(() => byDay(rows || []), [rows]);
  const losing = data.filter((d) => d.margin_idr < 0);
  return (
    <div className="aivora-card p-4" data-testid="vc-chart">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold text-slate-900">Biaya & margin harian</p>
        {rows && data.length > 0 && (losing.length ? <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-bold text-rose-700" data-testid="vc-chart-losing"><TrendingDown size={12} /> {losing.length} hari merugi</span>
          : <span className="text-[11px] text-slate-400" data-testid="vc-chart-ok">Tidak ada hari merugi</span>)}
      </div>
      {!rows ? <p className="mt-4 text-xs text-slate-400">Memuat grafik…</p> : !data.length ? <p className="mt-4 text-xs text-slate-400" data-testid="vc-chart-empty">Belum ada sesi video pada periode ini.</p> : (
        <>
          <div className="mt-3 h-72">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} initialDimension={{ width: 1, height: 1 }}>
              <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="day" fontSize={11} /><YAxis fontSize={11} tickFormatter={short} />
                <Tooltip content={<Tip />} /><Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke="#94a3b8" />
                <Bar dataKey="provider_cost_idr" name="Biaya provider" stackId="c" fill="#94a3b8" />
                <Bar dataKey="infra_cost_idr" name="Infra LiveKit" stackId="c" fill="#cbd5e1" />
                <Bar dataKey="margin_idr" name="Margin (merah = rugi)" fill="#10b981" radius={[3, 3, 0, 0]}>{data.map((d) => <Cell key={d.period} fill={d.margin_idr < 0 ? "#e11d48" : "#10b981"} />)}</Bar>
                <Line dataKey="revenue_idr" name="Pendapatan" stroke="#2563EB" strokeWidth={2} dot={{ r: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {losing.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="vc-chart-losing-days">
              {losing.map((d) => <span key={d.period} className="rounded-lg bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700">{d.period}: {idr(d.margin_idr)} · {d.sessions} sesi</span>)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
